import { useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Paginator } from "primereact/paginator";
import { format, isValid } from "date-fns";
import AlarmTypeChip from "./AlarmTypeChip";
import AlarmReadStatusChip from "./AlarmReadStatusChip";
import {
  AlarmListContainer,
  AlarmListItem,
  AlarmValue,
  AlarmTimestamp,
  AlarmTroubleshootingStep,
  AlarmTableHeader,
  AlarmHeaderCell,
  AlarmCardCell,
  AlarmIdBadge,
  AlarmTypeContainer,
  TableCard,
  TableToolbar,
  ToolbarCount,
  ToolbarFilters,
  FilteredBadge,
  TablePaginator,
} from "styles/components/deviceAlarms.styles";
import {
  getAlarmDisplayType,
  getFirstTroubleshootingStep,
} from "utils/deviceAlarmsData";
import { trackEvent } from "utils/analytics";
import { EmptyState } from "components/shared/EmptyState";
import { filteredEmpty, zeroData } from "utils/emptyCopy";

const AlarmTable = ({ alarms, onFixClick }: any) => {
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [filterType, setFilterType] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterText, setFilterText] = useState("");

  const handleFixClick = (alarm: any) => {
    trackEvent("alarm_interacted", {
      alarm_type: getAlarmDisplayType(alarm.errorId, alarm.type || "unknown"),
    });
    onFixClick(alarm);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return isValid(date) ? format(date, "MMM dd, yyyy h:mm a") : "Invalid Date";
  };

  const onPageChange = (event: any) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  const alarmTypeOptions = [
    { label: "All Types", value: "" },
    ...Array.from(
      new Set(
        (alarms || []).map((alarm: any) =>
          getAlarmDisplayType(alarm.errorId, alarm.type),
        ),
      ),
    ).map((type) => ({ label: type as string, value: type })),
  ];

  const statusOptions = [
    { label: "All Status", value: "" },
    { label: "Unread", value: "false" },
    { label: "Read", value: "true" },
  ];

  const filteredAlarms = (alarms || []).filter((alarm: any) => {
    const displayType = getAlarmDisplayType(alarm.errorId, alarm.type);

    if (filterType && displayType !== filterType) return false;
    if (filterStatus !== null && String(alarm.isRead) !== filterStatus)
      return false;
    if (filterText) {
      const search = filterText.toLowerCase();
      const formattedDate = formatDate(alarm.createdAt).toLowerCase();
      if (
        !displayType.toLowerCase().includes(search) &&
        !String(alarm.value).toLowerCase().includes(search) &&
        !formattedDate.includes(search)
      )
        return false;
    }
    return true;
  });

  const hasActiveFilters =
    !!filterType || filterStatus !== null || !!filterText;

  const clearFilters = () => {
    setFilterType(null);
    setFilterStatus(null);
    setFilterText("");
    setFirst(0);
  };

  const AlarmItem = ({ alarm }: { alarm: any }) => {
    const firstTroubleshootingStep = getFirstTroubleshootingStep(alarm.errorId);

    return (
      <AlarmListItem>
        <AlarmCardCell data-label="ID">
          <AlarmIdBadge>{alarm.id}</AlarmIdBadge>
        </AlarmCardCell>

        <AlarmCardCell data-label="Type">
          <AlarmTypeContainer>
            <AlarmTypeChip
              type={getAlarmDisplayType(alarm.errorId, alarm.type)}
            />
          </AlarmTypeContainer>
        </AlarmCardCell>

        <AlarmCardCell data-label="Value">
          <AlarmValue>{alarm.value}</AlarmValue>
        </AlarmCardCell>

        <AlarmCardCell data-label="First Action">
          <AlarmTroubleshootingStep>
            {firstTroubleshootingStep}
          </AlarmTroubleshootingStep>
        </AlarmCardCell>

        <AlarmCardCell data-label="Date">
          <AlarmTimestamp>{formatDate(alarm.createdAt)}</AlarmTimestamp>
        </AlarmCardCell>

        <AlarmCardCell data-label="Status">
          <AlarmReadStatusChip isRead={alarm.isRead} />
        </AlarmCardCell>

        <AlarmCardCell data-label="Action">
          <Button
            icon="pi pi-wrench"
            className="p-button-rounded p-button-outlined p-button-sm"
            onClick={() => handleFixClick(alarm)}
            tooltip="View Details"
            tooltipOptions={{ position: "top" }}
            style={{ width: "32px", height: "32px" }}
          />
        </AlarmCardCell>
      </AlarmListItem>
    );
  };

  if (!alarms || alarms.length === 0) {
    return <EmptyState message={zeroData("alarms")} />;
  }

  const currentPageAlarms = filteredAlarms.slice(first, first + rows);

  return (
    <TableCard>
      <TableToolbar>
        <ToolbarCount>
          <i className="pi pi-bell" />
          {filteredAlarms.length} alarm{filteredAlarms.length !== 1 ? "s" : ""}
          {hasActiveFilters && (
            <FilteredBadge>of {alarms.length} total</FilteredBadge>
          )}
        </ToolbarCount>

        <ToolbarFilters>
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              placeholder="Search..."
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
                setFirst(0);
              }}
            />
          </span>
          <Dropdown
            options={alarmTypeOptions}
            optionLabel="label"
            optionValue="value"
            value={filterType ?? ""}
            onChange={(e) => {
              setFilterType(e.value === "" ? null : e.value);
              setFirst(0);
            }}
            placeholder="All Types"
            style={{ minWidth: "150px" }}
          />
          <Dropdown
            options={statusOptions}
            optionLabel="label"
            optionValue="value"
            value={filterStatus === null ? "" : String(filterStatus)}
            onChange={(e) => {
              setFilterStatus(e.value === "" ? null : e.value);
              setFirst(0);
            }}
            placeholder="All Status"
            style={{ minWidth: "130px" }}
          />
          {hasActiveFilters && (
            <Button
              icon="pi pi-times"
              label="Clear"
              className="p-button-text p-button-sm"
              onClick={clearFilters}
            />
          )}
        </ToolbarFilters>
      </TableToolbar>

      <AlarmTableHeader>
        <AlarmHeaderCell>ID</AlarmHeaderCell>
        <AlarmHeaderCell>Alarm Type</AlarmHeaderCell>
        <AlarmHeaderCell>Value</AlarmHeaderCell>
        <AlarmHeaderCell>First Action</AlarmHeaderCell>
        <AlarmHeaderCell>Date & Time</AlarmHeaderCell>
        <AlarmHeaderCell>Status</AlarmHeaderCell>
        <AlarmHeaderCell>Action</AlarmHeaderCell>
      </AlarmTableHeader>

      {filteredAlarms.length === 0 ? (
        <EmptyState message={filteredEmpty("alarms")} />
      ) : (
        <AlarmListContainer>
          {currentPageAlarms.map((alarm: any) => (
            <AlarmItem key={alarm.id} alarm={alarm} />
          ))}
        </AlarmListContainer>
      )}

      <TablePaginator>
        <Paginator
          first={first}
          rows={rows}
          totalRecords={filteredAlarms.length}
          rowsPerPageOptions={[5, 10, 20, 50]}
          onPageChange={onPageChange}
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} alarms"
        />
      </TablePaginator>
    </TableCard>
  );
};

export default AlarmTable;
